import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { hasAnyRole } from './permissions';
import { ALL_CLIENT_ROLES } from '@/types/auth';
import type { Role } from '@/types/auth';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
  /** If set, only users with at least one of these roles may render the children. */
  requiredRoles?: Role[];
}

/**
 * Route guard.
 *
 * - Not yet hydrated: render nothing (avoids a one-frame redirect to /login
 *   while we read the JWT out of localStorage on first mount).
 * - Not authenticated: redirect to /login, preserving `from` so we can
 *   bounce back after a successful sign-in.
 * - Authenticated but missing role: redirect to /overview (the safe default
 *   landing page every authenticated user can reach).
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isInitializing, user } = useAuth();
  const location = useLocation();

  if (isInitializing) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (requiredRoles && requiredRoles.length > 0) {
    const allowed = user ? hasAnyRole(user.roles, requiredRoles) : false;
    if (!allowed) {
      // Client-role users navigating to a staff-only page land at their portal, not /unauthorized.
      const isClientUser = user ? hasAnyRole(user.roles, ALL_CLIENT_ROLES) : false;
      if (isClientUser) return <Navigate to="/portal/overview" replace />;
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
